import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum): the vortex IS the light,
// and every one of its points is additive - a stage rig in front of it would
// only wash the black it needs.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'maelstrom',
  family: 'particles',
  danceability: 0.85,
  // 0, not 1 - requiresTier gates SELECTION, not capability, and the radio
  // page measures tier 0 on the hardware it actually runs on. The TIER table
  // below has a real tier-0 layout; the frame-time governor still owns the
  // tier itself. Same reasoning as swarm.js/nebula.js.
  requiresTier: 0,
  energyLevel: 0.84,
  bpmMin: 110,
  bpmMax: 180,
  movement: 0.85,
  tonality: 0.65,
  prefers: { gridStrength: [0.55, 1.0] },
  enabled: true
};

// ============================================================================
// Maelstrom - an endless forward travelling through a vortex of light whose
// points are organised into logarithmic spiral arms.
//
// THE LAW OF ORGANISATION IS THE WHOLE VISUALIZER. Nothing here disperses
// points to make a vortex: every point carries one number, its position
// `u` along a spiral, and the spiral law turns that number into a radius,
// an angle and a depth. The swirl is read off the STRUCTURE, not off noise.
//
//   r(u)     = R0 * exp(B * u)          the logarithmic spiral
//   angle(u) = u + armOffset + twist(r) the arm it belongs to
//   depth(u) = D_FAR * exp(-ALPHA * u)   the travelling, see below
//
// WHICH END IS NEAR. The brief writes z = -k*theta, i.e. the spiral widening
// AWAY from the lens. Built that way the image has no vanishing point at all:
// every visible point sits at r/d > 1, there is no central core to travel
// toward, and the radius the camera itself sits at grows without bound, so the
// recycle can never be hidden. The brief's own picture - "le coeur ... c'est le
// point de fuite du travelling", near points huge at the edges, far points tiny
// at the centre - requires the opposite: the spiral OPENS TOWARD THE LENS.
// That is the one sign that is flipped here (k > 0 above).
//
// WHY THE DEPTH IS EXPONENTIAL AND NOT LINEAR. Built first with the brief's
// z = -k*theta, the image was wrong in a way that took a measurement to see:
// what the eye reads is not b but the APPARENT winding of the spiral on
// screen, and screen radius is r/depth. With a linear depth the last radian
// before the lens collapses the depth by 14x on its own, so a b of 0.20
// rendered as an apparent 0.46 - a spiral that spent 80% of its length inside
// the middle fifth of the frame and then threw three lonely beads across the
// rest of it. Making the depth exponential, depth = D_FAR * exp(-ALPHA*u),
// makes the apparent winding a CONSTANT, GAMMA = B + ALPHA, which is the
// number the brief's b actually names. It also makes the vortex self-similar,
// which is what lets the travelling loop at all (see droste-dive.js for the
// same identity used on a flat image).
//
// FORWARD, ALWAYS, AND THE WINDING IS RATE-LIMITED TO KEEP IT THAT WAY.
// Screen radius is rho = rho_pivot * exp(GAMMA * (u - U_PIVOT)), so
//
//   d(ln rho)/dt = GAMMA' * (u - U_PIVOT) + GAMMA * OMEGA_U
//
// The second term is the travelling and is always positive. The first is the
// bass moving the winding, and it is NEGATIVE for half the frame whichever way
// the winding goes - so a winding that moves fast enough drags points backwards
// through the frame, and the travelling stops reading as endless. Bounding
// |GAMMA'| <= 0.75 * GAMMA * OMEGA_U / U_PIVOT makes the whole expression
// positive for every visible u, at any bpm, under any band signal. Measured
// before and after on a four-on-the-floor kick with a drop: 16.5% of
// point-frames moving inward, then 0%.
//
// THE TRAVELLING IS A CHANGE OF REFERENCE FRAME, and that is what makes it
// infinite. Advancing the camera through a finite cell of vortex cannot loop;
// advancing every point's `u` by the same amount and wrapping it at THETA is
// the identical image and loops exactly. A point wraps from (u=THETA: nearest,
// widest, far outside the frame and already faded to black) to (u=0: the core,
// 95 units away, a single dim pixel). Measured: the points that wrap in a
// given frame were carrying 0.0014% of that frame's light.
//
// ROTATION IS A CLOSED FORM, NOT AN ACCUMULATOR. The brief asks for galactic
// differential rotation, w(r) = w0 / (1 + 0.35 r). A point's radius changes as
// it travels, so its accumulated angle is a path integral - but with u linear
// in time that integral has an exact primitive:
//
//   F(u) = u - ln(1 + a e^(B u)) / B,   a = 0.35 * r(0)
//   phi  = (w0 / OMEGA_U) * (F(u) - F(0))
//
// so d(angle)/dt = OMEGA_U + w0/(1 + 0.35 r): the law, exactly, with no state
// to integrate and nothing that grows without bound. It also resets itself on
// the wrap, invisibly, because F(0) = F(0). This is why nothing is computed
// CPU-side per point.
//
// PRECISION. The brief asks for mediump, and the fragment shader - where the
// cost actually is - is mediump. The VERTEX shader is not, and this is the one
// place it would have been wrong to follow the brief: on the television
// mediump is real fp16, where a value of magnitude M carries an ulp of M/1024,
// and here the spiral parameter u IS an angle that runs to 25 radians. Its ulp
// at fp16 is 0.0156 rad, which at the frame's edge is ten pixels of jitter on
// every point, every frame, as u advances. GLSL ES requires highp in vertex
// shaders precisely for positions like this. The fragment shader, whose inputs
// are all in [0, 1], stays mediump.
//
// Everything is still kept SMALL, because precision is not the only fp16 trap
// and the values cross a varying boundary:
//   u0 + uTravel <= 50.3      a*exp(B*u) <= 12 (194 for a star, unused)
//   exp(B*u) <= 15.2          depth <= 95        phi <= 12.6 (clamped)
//   arm index recovered as floor(x*9 + 0.5) from x = k/9, k in 0..8
// No unbounded clock reaches a shader either: uTwinkle, uSpin and uDither are
// all wrapped CPU-side before upload, and uTravel is wrapped at THETA_SPAN.
// ============================================================================

// --- the spiral ---------------------------------------------------------------
// Four turns per period. The whole period is not visible: the spiral leaves
// the frame around u = 21.2 and the last 4 radians happen off-screen, which is
// where the recycle hides.
const THETA_SPAN = 8 * Math.PI;     // 25.133
// The depth law. D_FAR sits inside the shared camera's far plane of 100 and
// D_NEAR well outside its near plane of 0.1.
const D_FAR = 95.0;
const D_NEAR = 2.0;
const LOG_DEPTH = Math.log(D_FAR / D_NEAR);          // 3.861
const ALPHA = LOG_DEPTH / THETA_SPAN;                // 0.1536, the depth's own pitch
// GAMMA is the brief's b: the winding of the spiral AS SEEN, which is what the
// eye reads and what the bass modulates. The world-space winding B is whatever
// is left once the depth has taken its share.
const GAMMA_BASE = 0.20;
// The floor is not a taste decision: below 0.194 the spiral no longer leaves
// the frame before its points wrap, and the recycle becomes visible.
const GAMMA_MIN = 0.194;
const GAMMA_MAX = 0.262;
const R0 = 1.66;                    // radius at the core end of one period
// The world is deliberately large. The projection does not care - scaling R0
// and D_FAR together gives the identical image - but w(r) = w0/(1 + 0.35 r)
// very much does: at a radius of order 1 that law is nearly constant and the
// rotation reads as rigid. See the differential-rotation note below.

// --- populations --------------------------------------------------------------
// Three scales, because one point size renders flat. The SIZE_ multipliers and
// the chain count are a continuity budget, measured rather than chosen:
//
//   A chain's neighbours are Du = THETA * N / count apart in u, and at the
//   frame's corner that is 1.02 * H * Du on screen. For the arm to stay a line
//   rather than a string of beads, that has to fit inside the sprite's LIT
//   core, which for a chain runs to 0.36 of a 15px sprite - 10.8 * H/720 px -
//   so Du <= 0.0186: 9 350 chains at 5 arms and 13 500 at 9. Hence 13 500, not
//   the brief's 3 000. Measured at 3 000 the outer arms were 19px gaps between
//   15px points, which is exactly the "points espacés" the first acceptance
//   criterion rules out. The budget is taken out of the dust, so the total is
//   still 40 000.
const SIZE_K = D_FAR;               // so a population's multiplier IS its size at D_FAR
const SIZE_CHAIN = 1.0;
const SIZE_CLOUD = 0.78;
const SIZE_DUST = 0.34;
const SIZE_STAR = 0.16;
// Both ends of this clamp are reached: a far cloud/dust/star point sits on the
// floor, and a chain reaches the ceiling at u = 17.6, halfway out across the
// frame and at full brightness. The brief asks for both "1 to 14px" and a 15:1
// ratio, which one clamp cannot give at once; the ratio is the acceptance
// criterion, so it is the ratio that is exact.
// The sizes are in 720p pixels and scaled by the real buffer height, so the
// ratio of sprite to gap - the thing continuity depends on - does not move
// when the governor changes the internal resolution.
const POINT_PX_MIN = 1.0;
const POINT_PX_MAX = 15.0;
const SIZE_REF_HEIGHT = 720;
// The colour ramp's domain, in r/depth: 0.012 is just inside the core and
// 1.35 just past the frame's corner (the frame's half-width is 1.20).
const SCREEN_R_MIN = 0.012;
const SCREEN_R_MAX = 1.35;
const CLOUD_SIGMA = 0.15;           // gaussian spread around the arm, x local radius
const DUST_R_MIN = 0.03;            // fraction of the local radius
const DUST_R_SPAN = 1.25;

const TIER = {
  // Chains are the structure and are the last thing to be cut; the dispersed
  // populations, which is where the fill rate goes, are cut first.
  0: { chains: 6000, cloud: 3000, dust: 0, stars: 0 },
  1: { chains: 9000, cloud: 4000, dust: 7000, stars: 0 },
  2: { chains: 13500, cloud: 9500, dust: 17000, stars: 400 }
};
const DEFAULT_TIER = 2;

// --- musical ------------------------------------------------------------------
const TRAVEL_BEATS_PER_TURN = 4;    // one turn of spiral crossed per 4 beats
const ROT_BEATS_PER_TURN_MID = 16;  // a point at mid-radius turns once per 16
const ROT_R_COEF = 0.35;            // w(r) = w0 / (1 + 0.35 r)
const TWIST_MAX = 0.55;             // see the precision note in the header
const WAVE_BEATS = 2;               // core -> maximum radius, exactly
const WAVE_FALL = 7.0;              // how fast a point falls back after the front
const ARM_CHOICES = [3, 5, 7, 9];
const ARM_DEFAULT = 5;
const ARM_MIX_BEATS = 2;            // points migrate to their new arm over 2 beats
const RAMP_STEPS = 8;               // one notch of colour ramp per bar, 8-bar cycle
const RAMP_SLEW_S = 0.15;           // the notch is a step, not a fade - but not a flicker
const CORE_PULSE_AMOUNT = 0.3;
const CORE_PULSE_DECAY = 10;
const TIGHTEN_BEATS = 4;            // the winding closes ahead of a drop
const TIGHTEN_B = 1.35;
const DROP_BEATS = 1.6;
// THE TRAVELLING ONLY EVER GOES FORWARD. The brief asks for the camera to back
// off on a drop; measured, that made 100% of the points on screen move inward
// for 0.4 s, which is the one thing a never-ending forward travelling cannot
// do. A drop now SURGES instead - still forward, just faster - and the change
// of rotation sense carries the rest of the gesture.
const DROP_SURGE = 1.95;
// Where the winding pivots, and the lever arm the rate limit is computed from.
// Half the period, NOT half the visible range: a dust point near the axis is
// still inside the frame at u = THETA_SPAN, and picking the visible range
// instead left exactly those points with a lever arm 37% longer than the bound
// assumed - measured, 0.5% of them still crept inward. Half the period is the
// largest |u - U_PIVOT| any point can have, so the bound is exact.
const U_PIVOT = THETA_SPAN / 2;
// How much of the travelling's own outward speed the winding is allowed to eat.
// See _slewGamma: at 0.85 every visible point still moves outward at 15% of its
// normal rate in the worst case, and never inward.
const RECEDE_SAFETY = 0.85;
const GAMMA_SLEW_S = 0.35;

// --- camera / framing ---------------------------------------------------------
const DRIFT_AMP = 0.3;
const DRIFT_PERIOD_S = 37;
// A fixed world offset of the vortex axis is exactly a lateral camera move, so
// this is real parallax: it shifts a point at depth 1 by 240px and one at the
// vanishing point by 6px. The centre of the vortex stops being the centre of
// the frame without anything tilting.
const SAFE_FLOOR = 0.45;
// The fade starts exactly where the spiral leaves the frame (depth 3.75, u =
// 21.1) and is complete at the wrap, which is 4 radians of u - 1.2 s - later.
// It is what makes the recycle invisible for the only points still inside the
// frame that late: the dust near the axis.
const NEAR_FADE_LO = 2.05;
const NEAR_FADE_HI = 3.75;
const ATMO_START = 5.0;
const ATMO_RATE = 0.0105;           // 0.18 of the light survives at the far end
const ATMO_FLOOR = 0.18;
const CORE_DEPTH = 55.0;            // where the halo sits: at the vanishing point
const CORE_HALO_HALF = 17.5;        // ~170px of radius

// --- stars --------------------------------------------------------------------
const STAR_R_MIN = 2.5;             // never on the axis, so they never wrap on screen
const STAR_R_MAX = 22.0;
const STAR_DIM = 0.45;

const RAMP_TEXELS = 64;
const DEFAULT_PALETTE = ['#ffb347', '#ff4f9a', '#7a5cff', '#2ad0ff'];

const TAU = Math.PI * 2;
const _size = new THREE.Vector2();

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function clamp(x, lo, hi) { return Math.max(lo, Math.min(hi, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
// Box-Muller, clipped: the cloud's spread has to be gaussian (the brief) but
// a stray 4-sigma point 60% off its arm would read as a bug, not as thickness.
function gauss(rand) {
  const u = Math.max(1e-6, rand());
  const v = rand();
  return clamp(Math.sqrt(-2 * Math.log(u)) * Math.cos(TAU * v), -2.5, 2.5);
}
// Deterministic per-instance stream, so two Maelstroms in one session are not
// bit-identical and a single one is reproducible across a tier rebuild.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ============================================================================
// The points. One draw call, everything analytic, nothing uploaded per frame.
// ============================================================================
const pointsVertexShader = `
  precision highp float;

  // position = (u0, population, sizeScale)
  //   population: 0 chains, 1 cloud, 2 dust, 3 stars
  attribute vec3 aScatter;   // (radial factor | absolute radius for stars,
                             //  angular offset, depth scatter)
  attribute vec4 aArm;       // this point's arm index / 9, one component per
                             // possible arm count (3, 5, 7, 9)
  attribute float aSeed;

  uniform float uB;
  uniform float uR0;
  uniform float uTheta;
  uniform float uAlpha;
  uniform float uDFar;
  uniform float uTravel;
  uniform float uArmsA;
  uniform float uArmsB;
  uniform float uArmMix;
  uniform vec4 uSelA;
  uniform vec4 uSelB;
  uniform float uTwist;
  uniform float uSpin;
  uniform vec2 uAxis;
  uniform float uSizeK;
  uniform float uSizeScale;
  uniform float uBeatPhase;
  uniform float uWaveAmt;
  uniform float uCorePulse;
  uniform float uRampShift;
  uniform float uLogRMin;
  uniform float uLogRSpan;
  uniform float uIntensity;
  uniform float uActiveFrac;
  uniform float uDustGain;
  uniform float uSparkle;
  uniform float uTwinkle;
  uniform vec4 uPopMask;
  uniform sampler2D uRamp;

  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  varying vec3 vColor;
  varying float vAlpha;
  varying float vWide;

  const float TAU = 6.2831853;

  void main() {
    float u0  = position.x;
    float pop = position.y;

    float isStar  = step(2.5, pop);
    float isDust  = step(1.5, pop) - isStar;
    float isCloud = step(0.5, pop) - isDust - isStar;
    float isChain = 1.0 - step(0.5, pop);
    float isArm   = isChain + isCloud;
    float dyn     = 1.0 - isStar;          // stars do not turn with the vortex

    // --- where this point is along its period -------------------------------
    // The travelling: one shift for every point, wrapped at THETA. This is the
    // camera advancing, expressed in the vortex's own frame.
    float u = mod(u0 + uTravel, uTheta);

    // --- the spiral law ------------------------------------------------------
    float growth = exp(uB * u);
    float rSpiral = uR0 * aScatter.x * growth;
    float r = mix(rSpiral, aScatter.x, isStar);

    // --- the arm ------------------------------------------------------------
    // Arm index recovered exactly from k/9 (k <= 8), so the arm count can
    // change without a single byte being re-uploaded. The interpolation takes
    // the short way round, or a point leaving arm 0 for arm N-1 would swing
    // the whole way across the vortex instead of one notch back.
    float idxA = floor(dot(aArm, uSelA) * 9.0 + 0.5);
    float idxB = floor(dot(aArm, uSelB) * 9.0 + 0.5);
    float offA = idxA * (TAU / uArmsA);
    float offB = idxB * (TAU / uArmsB);
    float dOff = mod(offB - offA + 3.14159265, TAU) - 3.14159265;
    float arm = (offA + dOff * uArmMix) * isArm;

    // --- differential rotation, in closed form -------------------------------
    // phi is the exact integral of w(r) = w0/(1+0.35 r) along this point's own
    // trajectory. a is 0.35 * its radius at u = 0, which is all that changes
    // between populations - so dust near the axis really does turn ~3x faster
    // than the rim, from one expression.
    float a = ROT_R_COEF_C * uR0 * aScatter.x;
    float phi = uTwist * (u - (log(1.0 + a * growth) - log(1.0 + a)) / uB);

    float angle = dyn * (u + arm + phi + uSpin) + aScatter.y;

    // --- depth ---------------------------------------------------------------
    // Exponential, so the spiral's APPARENT winding (r/depth) is the constant
    // uB + uAlpha rather than something that doubles as the lens closes in.
    // u = uTheta is the nearest slice, u = 0 the core at the vanishing point.
    float depth = uDFar * exp(-uAlpha * u) + CLOUD_SIGMA_C * r * aScatter.z;
    depth = max(depth, 0.5);

    vec3 P = vec3(r * cos(angle) + uAxis.x, r * sin(angle) + uAxis.y, -depth);

    vec4 mv = modelViewMatrix * vec4(P, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = clamp(uSizeK * position.z / depth, POINT_MIN_C, POINT_MAX_C) * uSizeScale;

    // --- colour: a radial ramp, not a per-point colour -----------------------
    // The radius that matters is the one on SCREEN, r/depth: "au coeur" and
    // "en peripherie" are places in the frame, and two points that sit next to
    // each other in the picture have to share a colour, which is the fifth
    // acceptance criterion. Measured against world radius instead, the whole
    // visible frame fell inside 30% of the ramp and the image was one hue.
    // Logarithmic, because screen radius is exp(GAMMA*u): this makes the ramp
    // uniform ACROSS THE FRAME rather than crushed into the outer turn.
    float tRad = clamp((log(max(r / depth, 0.0008)) - uLogRMin) / uLogRSpan, 0.0, 1.0);
    vec3 base = texture2D(uRamp, vec2(fract(tRad - uRampShift), 0.5)).rgb;
    base *= 0.92 + 0.16 * aSeed;

    // The core stays near-white however far the ramp has migrated, and the
    // periphery darkens - the two ends of the brief's gradient, which are
    // about luminance, not hue, and so survive the ramp rotating under them.
    float coreW = 1.0 - smoothstep(0.0, 0.22, tRad);
    vec3 col = mix(base, vec3(1.0), coreW * 0.80);
    col *= mix(1.0, 0.45, smoothstep(0.55, 1.0, tRad));
    // The stars are not part of the vortex and do not take its colour: they
    // are the fixed, very fine scale against which it is read.
    col = mix(col, vec3(0.86, 0.90, 1.0), isStar * 0.9);

    // --- the wave along the arms ---------------------------------------------
    // One front launched per beat, crossing the whole radius range in exactly
    // WAVE_BEATS. Sharp attack on passage, exponential fall after it. Two
    // fronts are alive at any instant, which is what fract() gives for free.
    float wd = fract(uBeatPhase - WAVE_RATE_C * tRad);
    float wave = exp(-wd * WAVE_FALL_C);
    float bright = 1.0 + uWaveAmt * wave * (isArm + isDust * 0.25);
    bright += uCorePulse * coreW;
    bright += uSparkle * (0.5 + 0.5 * sin(uTwinkle + aSeed * TAU)) * (isDust + isStar);
    bright *= mix(1.0, uDustGain, isDust);
    bright *= mix(1.0, STAR_DIM_C, isStar);

    // --- depth cues ----------------------------------------------------------
    float atmo = clamp(1.0 - (depth - ATMO_START_C) * ATMO_RATE_C, ATMO_FLOOR_C, 1.0);
    // The near fade is what makes the recycle invisible for the points that
    // are still inside the frame when they reach it - the dust near the axis.
    // Everything on an arm has left the frame long before this bites.
    float nearFade = smoothstep(NEAR_LO_C, NEAR_HI_C, depth);

    // --- how much of the vortex is alive at all (progress) -------------------
    // Decorrelated from aSeed, or the culled points would all be the ones
    // carrying the same colour jitter.
    float lifeKey = fract(aSeed * 7.31 + 0.137);
    float alive = 1.0 - smoothstep(uActiveFrac - 0.06, uActiveFrac, lifeKey);
    alive = mix(1.0, alive, isCloud + isDust);
    alive *= dot(uPopMask, vec4(isChain, isCloud, isDust, isStar));

    vec2 ndc = gl_Position.xy / max(gl_Position.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);

    vColor = col * bright * atmo * nearFade * alive * uIntensity;
    vAlpha = mix(SAFE_FLOOR_C, 1.0, smoothstep(0.0, uSafeFeather, sd));
    vWide = isChain;
  }
`;

const pointsFragmentShader = `
  precision mediump float;
  varying vec3 vColor;
  varying float vAlpha;
  varying float vWide;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float r2 = dot(d, d);
    if (r2 > 0.25) discard;
    // The chains are the structure, so they are the ones that bloom - and the
    // shape of that bloom is what decides whether an arm is a line or a string
    // of beads. Measured: with the tight core everything else uses, a 15px
    // chain sprite lights only its middle 4px and 8px-spaced neighbours still
    // read as dots. The chain's lit core runs out to 0.36 of its sprite and
    // its halo to the edge, so neighbours overlap. Everything else keeps the
    // tight profile, or 30 000 additive points would fog the black the whole
    // image is read against.
    float halo = 1.0 - smoothstep(0.0, mix(0.11, 0.25, vWide), r2);
    float core = 1.0 - smoothstep(0.0, mix(0.010, 0.130, vWide), r2);
    vec3 c = vColor * halo * mix(0.45, 0.62, vWide)
           + mix(vColor, vec3(1.0), 0.40) * core * mix(0.85, 0.60, vWide);
    gl_FragColor = vec4(c * vAlpha, 1.0);
  }
`;

// ============================================================================
// Background: absolute black, dithered. An ordered 4x4 pattern rather than a
// sin() hash - on fp16 a hash fed gl_FragCoord collapses into flat bands,
// which is exactly the artefact the dither exists to remove. Every value here
// stays under 20 and is exact.
// ============================================================================
const bgVertexShader = `
  precision mediump float;
  void main() { gl_Position = vec4(position.xy * 2.0, 0.999, 1.0); }
`;
const bgFragmentShader = `
  precision mediump float;
  uniform float uDither;
  void main() {
    vec2 q = mod(gl_FragCoord.xy, 4.0);
    float n = mod(q.x * 5.0 + q.y * 3.0 + uDither, 4.0) * 0.25;
    gl_FragColor = vec4(vec3(n * ONE_255_C), 1.0);
  }
`;

// ============================================================================
// The core's halo. The chains piling up at u -> 0 already make a near-white
// mass; this is the glow around it. It sits on the axis at the vanishing
// point, so it lands behind the cover - which is the framing the brief wants,
// and why it carries the safe-zone attenuation per fragment (the quad is far
// too large for the shared per-vertex chunk to mean anything on it).
// ============================================================================
const coreVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  varying vec2 vScreen;
  uniform float uAspect;
  void main() {
    vUv = uv;
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    vec2 ndc = clip.xy / max(clip.w, 0.0001);
    vScreen = vec2(ndc.x * uAspect, ndc.y);
    gl_Position = clip;
  }
`;
const coreFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uAmount;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  varying vec2 vScreen;
  void main() {
    float d = length(vUv - 0.5) * 2.0;
    float s = max(1.0 - d, 0.0);
    float g = s * s * (0.35 + 0.65 * s) + exp(-d * d * 30.0) * 0.75;
    vec2 q = abs(vScreen - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float safe = mix(SAFE_FLOOR_C, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(uColor * g * uAmount * safe, 1.0);
  }
`;

// Shader-source constants. Kept out of the template bodies above so the source
// reads as GLSL rather than as string surgery, and substituted once here.
function bakeConstants(src) {
  return src
    .replace(/ROT_R_COEF_C/g, ROT_R_COEF.toFixed(3))
    .replace(/CLOUD_SIGMA_C/g, CLOUD_SIGMA.toFixed(3))
    .replace(/STAR_DIM_C/g, STAR_DIM.toFixed(2))
    .replace(/POINT_MIN_C/g, POINT_PX_MIN.toFixed(2))
    .replace(/POINT_MAX_C/g, POINT_PX_MAX.toFixed(2))
    .replace(/WAVE_RATE_C/g, WAVE_BEATS.toFixed(2))
    .replace(/WAVE_FALL_C/g, WAVE_FALL.toFixed(2))
    .replace(/ATMO_START_C/g, ATMO_START.toFixed(2))
    .replace(/ATMO_RATE_C/g, ATMO_RATE.toFixed(4))
    .replace(/ATMO_FLOOR_C/g, ATMO_FLOOR.toFixed(2))
    .replace(/NEAR_LO_C/g, NEAR_FADE_LO.toFixed(2))
    .replace(/NEAR_HI_C/g, NEAR_FADE_HI.toFixed(2))
    .replace(/SAFE_FLOOR_C/g, SAFE_FLOOR.toFixed(2))
    .replace(/ONE_255_C/g, (1 / 255).toFixed(6));
}

const POINTS_VS = bakeConstants(pointsVertexShader);
const POINTS_FS = bakeConstants(pointsFragmentShader);
const BG_FS = bakeConstants(bgFragmentShader);
const CORE_FS = bakeConstants(coreFragmentShader);

export class MaelstromVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;

    const armsParam = parseInt(urlParam('maelarms'), 10);
    this._forcedArms = ARM_CHOICES.indexOf(armsParam) !== -1 ? armsParam : null;
    const bParam = parseFloat(urlParam('maelb'));
    this._forcedB = isFinite(bParam) ? bParam : null;
    const speedParam = parseFloat(urlParam('maelspeed'));
    // A multiplier on the calibrated speed: 1 is "one turn per 4 beats".
    this._speedScale = isFinite(speedParam) ? clamp(speedParam, 0.05, 8) : 1;
    this._chainsOnly = urlParam('maelchains') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const layout = TIER[this._tier];
    this._layout = layout;

    // The camera sits at the origin looking down -z and stays there: the
    // vortex travels past it (see the header). The lateral drift is applied to
    // the vortex's axis instead, which is the same parallax and keeps `depth`
    // exactly equal to the spiral law's own depth.
    camera.position.set(0, 0, 0);
    camera.quaternion.identity();
    camera.updateMatrixWorld(true);

    this._rand = mulberry32(0x9e3779b9 ^ Math.floor(Math.random() * 0xffffffff));

    this._buildRamp(null);
    this._buildPoints(layout);
    this._buildBackground();
    this._buildCore();

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._travel = 0;
    this._spin = 0;
    this._ditherFrame = 0;
    this._gamma = this._forcedB != null ? this._forcedB : GAMMA_BASE;
    this._gammaTarget = this._gamma;
    this._lowSmooth = 0.5;
    this._midSmooth = 0.5;
    this._highSmooth = 0.5;
    this._arms = this._forcedArms != null ? this._forcedArms : ARM_DEFAULT;
    this._armsPrev = this._arms;
    this._armMix = 1;
    this._armMixRate = 0;
    this._lastPhraseIndex = null;
    this._lastBarIndex = null;
    this._noGridPhraseAt = 0;
    this._noGridBarAt = 0;
    this._rampShift = 0;
    this._rampTarget = 0;
    this._lastPaletteRef = undefined;
    this._twistSign = 1;
    this._twist = 0;
    this._travelBoost = 1;
    this._travelBoostTarget = 1;
    this._pendingDropAt = null;
    this._tightenUntil = -1;
    this._surgeUntil = -1;

    this._applyArmSelectors();
    this._pointUniforms.uB.value = this._gamma - ALPHA;
    this._pointUniforms.uR0.value = R0 * Math.exp((GAMMA_BASE - this._gamma) * U_PIVOT);
    this._refreshRadiusRange();
  }

  // --------------------------------------------------------------------------
  // Geometry. Every attribute here is static for the life of the visualizer:
  // the arm count, the winding, the travelling and the rotation are all
  // uniforms, so nothing is ever re-uploaded.
  // --------------------------------------------------------------------------
  _buildPoints(layout) {
    const total = layout.chains + layout.cloud + layout.dust + layout.stars;
    this._count = total;
    const pos = new Float32Array(total * 3);
    const scatter = new Float32Array(total * 3);
    const arm = new Float32Array(total * 4);
    const seed = new Float32Array(total);
    const rand = this._rand;

    let w = 0;

    // CHAINS. Regularly spaced in u, and their arm index is `i mod N` for
    // EVERY candidate N at once - stored as four components, one per possible
    // arm count. That is what keeps an arm's own points evenly spaced whatever
    // N the phrase picks, without recomputing anything.
    for (let i = 0; i < layout.chains; i++, w++) {
      pos[w * 3] = (i / layout.chains) * THETA_SPAN;
      pos[w * 3 + 1] = 0;
      pos[w * 3 + 2] = SIZE_CHAIN;
      scatter[w * 3] = 1;
      scatter[w * 3 + 1] = 0;
      scatter[w * 3 + 2] = 0;
      for (let k = 0; k < 4; k++) arm[w * 4 + k] = (i % ARM_CHOICES[k]) / 9;
      seed[w] = rand();
    }

    // CLOUD. Gaussian around the arm - 15% of the local radius radially, the
    // same 15% as arc (0.15 rad of angle IS 0.15 r of arc) and in depth.
    for (let i = 0; i < layout.cloud; i++, w++) {
      pos[w * 3] = rand() * THETA_SPAN;
      pos[w * 3 + 1] = 1;
      pos[w * 3 + 2] = SIZE_CLOUD;
      scatter[w * 3] = Math.max(0.15, 1 + CLOUD_SIGMA * gauss(rand));
      scatter[w * 3 + 1] = CLOUD_SIGMA * gauss(rand);
      scatter[w * 3 + 2] = gauss(rand);
      for (let k = 0; k < 4; k++) arm[w * 4 + k] = Math.floor(rand() * ARM_CHOICES[k]) / 9;
      seed[w] = rand();
    }

    // DUST. No arm at all: a free angle and a radius anywhere from the axis to
    // just past the rim, biased inward (s squared) so there is always dust
    // near the axis - which, near the lens, is what crosses the frame huge and
    // out of focus and gives the travelling its speed.
    for (let i = 0; i < layout.dust; i++, w++) {
      const s = rand();
      pos[w * 3] = rand() * THETA_SPAN;
      pos[w * 3 + 1] = 2;
      pos[w * 3 + 2] = SIZE_DUST;
      scatter[w * 3] = DUST_R_MIN + DUST_R_SPAN * s * s;
      scatter[w * 3 + 1] = rand() * TAU;
      scatter[w * 3 + 2] = gauss(rand) * 0.6;
      for (let k = 0; k < 4; k++) arm[w * 4 + k] = 0;
      seed[w] = rand();
    }

    // STARS. Fixed in the world: they do not turn with the vortex and their
    // radius does not grow with it. They still travel, because the camera does
    // - which is the whole point of them. Kept off the axis so that the one
    // moment they wrap they are far outside the frame.
    for (let i = 0; i < layout.stars; i++, w++) {
      pos[w * 3] = rand() * THETA_SPAN;
      pos[w * 3 + 1] = 3;
      pos[w * 3 + 2] = SIZE_STAR;
      scatter[w * 3] = STAR_R_MIN + Math.sqrt(rand()) * (STAR_R_MAX - STAR_R_MIN);
      scatter[w * 3 + 1] = rand() * TAU;
      scatter[w * 3 + 2] = 0;
      for (let k = 0; k < 4; k++) arm[w * 4 + k] = 0;
      seed[w] = rand();
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aScatter', new THREE.BufferAttribute(scatter, 3));
    geo.setAttribute('aArm', new THREE.BufferAttribute(arm, 4));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, -20), 60);

    this._pointUniforms = Object.assign({}, safeZoneUniforms, {
      uB: { value: GAMMA_BASE - ALPHA },
      uR0: { value: R0 },
      uTheta: { value: THETA_SPAN },
      uAlpha: { value: ALPHA },
      uDFar: { value: D_FAR },
      uTravel: { value: 0 },
      uArmsA: { value: ARM_DEFAULT },
      uArmsB: { value: ARM_DEFAULT },
      uArmMix: { value: 1 },
      uSelA: { value: new THREE.Vector4(0, 1, 0, 0) },
      uSelB: { value: new THREE.Vector4(0, 1, 0, 0) },
      uTwist: { value: 0 },
      uSpin: { value: 0 },
      uAxis: { value: new THREE.Vector2(0, 0) },
      uSizeK: { value: SIZE_K },
      uSizeScale: { value: 1 },
      uBeatPhase: { value: 0 },
      uWaveAmt: { value: 0.9 },
      uCorePulse: { value: 0 },
      uRampShift: { value: 0 },
      uLogRMin: { value: 0 },
      uLogRSpan: { value: 1 },
      uIntensity: { value: 1 },
      uActiveFrac: { value: 1 },
      uDustGain: { value: 1 },
      uSparkle: { value: 0.3 },
      uTwinkle: { value: 0 },
      uPopMask: { value: new THREE.Vector4(1, 1, 1, 1) },
      uRamp: { value: this._rampTex }
    });
    if (this._chainsOnly) this._pointUniforms.uPopMask.value.set(1, 0, 0, 0);

    this._pointsMat = new THREE.ShaderMaterial({
      uniforms: this._pointUniforms,
      vertexShader: POINTS_VS,
      fragmentShader: POINTS_FS,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });
    this._points = new THREE.Points(geo, this._pointsMat);
    this._points.frustumCulled = false;
    const self = this;
    this._points.onBeforeRender = function (renderer) {
      // Sizes are quoted at 720p and scaled by the real buffer, so the sprite
      // never loses its race against the gap when the governor cuts the
      // internal resolution - see the continuity budget above.
      const t = renderer.getRenderTarget();
      const h = t ? t.height : renderer.getDrawingBufferSize(_size).y;
      self._pointUniforms.uSizeScale.value = Math.max(0.35, h / SIZE_REF_HEIGHT);
    };
    this._scene.add(this._points);
  }

  _buildBackground() {
    this._bgUniforms = { uDither: { value: 0 } };
    this._bgMat = new THREE.ShaderMaterial({
      uniforms: this._bgUniforms,
      vertexShader: bgVertexShader,
      fragmentShader: BG_FS,
      depthTest: false,
      depthWrite: false
    });
    this._bg = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._bgMat);
    this._bg.frustumCulled = false;
    this._bg.renderOrder = -10;
    this._scene.add(this._bg);
  }

  _buildCore() {
    this._coreUniforms = {
      uColor: { value: new THREE.Color(1, 0.94, 0.86) },
      uAmount: { value: 1 },
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._coreMat = new THREE.ShaderMaterial({
      uniforms: this._coreUniforms,
      vertexShader: coreVertexShader,
      fragmentShader: CORE_FS,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });
    // The camera never rotates, so a quad in the xy plane already faces it -
    // no billboarding to do and nothing to update per frame but its position.
    this._core = new THREE.Mesh(new THREE.PlaneGeometry(CORE_HALO_HALF * 2, CORE_HALO_HALF * 2), this._coreMat);
    this._core.position.set(0, 0, -CORE_DEPTH);
    this._core.frustumCulled = false;
    this._core.renderOrder = -5;
    this._scene.add(this._core);
  }

  // --------------------------------------------------------------------------
  // The colour ramp. A cyclic walk of the artwork's vivid colours around the
  // hue wheel, rotated so the warmest sits at the core. Cyclic because the
  // ramp MIGRATES one notch per bar: a ramp with ends would drag a seam
  // through the frame every bar, and the wheel has no ends.
  // --------------------------------------------------------------------------
  _buildRamp(vivid) {
    const src = (vivid && vivid.length ? vivid : DEFAULT_PALETTE).slice(0, 6);
    const cols = src.map(function (c) { return new THREE.Color(c); });
    const hsl = { h: 0, s: 0, l: 0 };
    const withHue = cols.map(function (c) {
      c.getHSL(hsl);
      return { c: c, h: hsl.h, s: hsl.s, l: hsl.l };
    });
    withHue.sort(function (a, b) { return a.h - b.h; });
    // Warmth as distance from orange (hue 0.08) around the wheel, so the
    // rotation lands the warmest colour on the core whatever the artwork is.
    let warmest = 0;
    let best = Infinity;
    for (let i = 0; i < withHue.length; i++) {
      let d = Math.abs(withHue[i].h - 0.08);
      if (d > 0.5) d = 1 - d;
      if (d < best) { best = d; warmest = i; }
    }
    const order = [];
    for (let i = 0; i < withHue.length; i++) order.push(withHue[(warmest + i) % withHue.length].c);

    const data = this._rampData || (this._rampData = new Uint8Array(RAMP_TEXELS * 4));
    const n = order.length;
    const a = new THREE.Color();
    for (let i = 0; i < RAMP_TEXELS; i++) {
      const x = (i / RAMP_TEXELS) * n;
      const i0 = Math.floor(x) % n;
      const i1 = (i0 + 1) % n;
      const f = x - Math.floor(x);
      a.copy(order[i0]).lerp(order[i1], f);
      data[i * 4] = Math.round(clamp01(a.r) * 255);
      data[i * 4 + 1] = Math.round(clamp01(a.g) * 255);
      data[i * 4 + 2] = Math.round(clamp01(a.b) * 255);
      data[i * 4 + 3] = 255;
    }
    if (!this._rampTex) {
      this._rampTex = new THREE.DataTexture(data, RAMP_TEXELS, 1, THREE.RGBAFormat);
      this._rampTex.wrapS = THREE.RepeatWrapping;
      this._rampTex.minFilter = THREE.LinearFilter;
      this._rampTex.magFilter = THREE.LinearFilter;
    }
    this._rampTex.needsUpdate = true;
    // The core's halo takes the warmest colour, lifted toward white.
    if (this._coreUniforms) {
      this._coreUniforms.uColor.value.copy(order[0]).lerp(new THREE.Color(1, 1, 1), 0.6);
    }
  }

  // Which component of aArm answers for the current arm count.
  _applyArmSelectors() {
    const u = this._pointUniforms;
    const iA = ARM_CHOICES.indexOf(this._armsPrev);
    const iB = ARM_CHOICES.indexOf(this._arms);
    u.uSelA.value.set(iA === 0 ? 1 : 0, iA === 1 ? 1 : 0, iA === 2 ? 1 : 0, iA === 3 ? 1 : 0);
    u.uSelB.value.set(iB === 0 ? 1 : 0, iB === 1 ? 1 : 0, iB === 2 ? 1 : 0, iB === 3 ? 1 : 0);
    u.uArmsA.value = this._armsPrev;
    u.uArmsB.value = this._arms;
  }

  // The ramp's domain is the frame itself - from just inside the core to just
  // past the corner - so it does not move when the winding does. Only the
  // rotation's calibration radius has to be refreshed.
  _refreshRadiusRange() {
    this._pointUniforms.uLogRMin.value = Math.log(SCREEN_R_MIN);
    this._pointUniforms.uLogRSpan.value = Math.log(SCREEN_R_MAX / SCREEN_R_MIN);
    this._rMid = this._pointUniforms.uR0.value * Math.exp((this._gamma - ALPHA) * THETA_SPAN) * 0.5;
  }

  // The winding's slew rate, bounded so that no point anywhere in the frame can
  // be dragged backwards by it. Anything that wants to move the winding - the
  // bass, the pre-drop tightening, ?maelb - goes through here.
  _slewGamma(dt, omegaU) {
    const eased = (this._gammaTarget - this._gamma) * (1 - Math.exp(-dt / GAMMA_SLEW_S));
    const cap = RECEDE_SAFETY * this._gamma * omegaU / U_PIVOT * dt;
    this._gamma += clamp(eased, -cap, cap);
    this._pointUniforms.uB.value = this._gamma - ALPHA;
    // Changing the pitch pivots about U_PIVOT rather than about the core, and
    // for this law that is exactly a scalar on R0 - one uniform, no new maths
    // in the shader.
    this._pointUniforms.uR0.value = R0 * Math.exp((GAMMA_BASE - this._gamma) * U_PIVOT);
    this._refreshRadiusRange();
  }

  _pickArms() {
    const others = ARM_CHOICES.filter((n) => n !== this._arms);
    return others[Math.floor(this._rand() * others.length)];
  }

  onTrackChange() {
    this._lastPaletteRef = undefined;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;   // freezes the travelling, the rotation and the waves
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;

    const u = this._pointUniforms;
    const director = profile.director;
    // ABSENT IS NOT ZERO. Without an analysis timeline director.bands is
    // {0,0,0} rather than null, and read literally that is "no bass at all
    // forever" - which would park the spiral at its most open for the whole of
    // any radio stream. hasReliableGrid is the flag that tells the two apart;
    // with no grid every band stands at its midpoint, which is exactly where
    // the brief's own numbers live.
    const bands = director && director.bands;
    const graded = !!(director && director.hasReliableGrid) && bands;
    const low = graded && bands.low != null ? clamp01(bands.low) : 0.5;
    const mid = graded && bands.mid != null ? clamp01(bands.mid) : 0.5;
    const high = graded && bands.high != null ? clamp01(bands.high) : 0.5;
    const k = 1 - Math.exp(-dt / 0.2);
    this._lowSmooth += (low - this._lowSmooth) * k;
    this._midSmooth += (mid - this._midSmooth) * k;
    this._highSmooth += (high - this._highSmooth) * k;

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 120;
    const beatS = 60 / bpm;

    // --- palette -------------------------------------------------------------
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._buildRamp(profile.artworkPalette && profile.artworkPalette.vividColors);
    }

    // --- drops: the winding closes for 4 beats, then the vortex inverts its
    // rotation and the travelling surges - forward, never back ---------------
    // director.nextEvent carries only {type, score, msUntil} and flips to the
    // following mark the instant one passes, so the drop's arrival is latched
    // in this visualizer's own clock - same pattern as swarm.js.
    const nextEvent = director && director.nextEvent;
    if (nextEvent && (nextEvent.type === 'drop' || nextEvent.type === 'rise')) {
      const until = nextEvent.msUntil / 1000;
      if (until > 0 && until <= TIGHTEN_BEATS * beatS) {
        this._pendingDropAt = this._time + until;
        this._tightenUntil = this._pendingDropAt;
      }
    }
    if (this._pendingDropAt != null && this._time >= this._pendingDropAt) {
      this._pendingDropAt = null;
      this._surgeUntil = this._time + DROP_BEATS * beatS;
      this._twistSign = -this._twistSign;       // the sense of rotation inverts
      this._travelBoostTarget = DROP_SURGE;     // and the travelling lurches forward
    }
    if (this._surgeUntil > 0 && this._time >= this._surgeUntil) {
      this._surgeUntil = -1;
      this._travelBoostTarget = 1;
    }
    const tightening = this._time < this._tightenUntil;

    // --- winding (bands.low) -------------------------------------------------
    // The bass TIGHTENS the spiral, which is a smaller gamma: more turns for
    // the same radius. The clamp is load-bearing - see GAMMA_MIN.
    if (this._forcedB != null) {
      this._gammaTarget = clamp(this._forcedB, GAMMA_MIN, GAMMA_MAX);
    } else {
      // Asymmetric on purpose, so a neutral bass sits exactly on the brief's
      // b = 0.20 and the two halves of the band each get their own swing.
      const g = this._lowSmooth <= 0.5
        ? lerp(GAMMA_MAX, GAMMA_BASE, this._lowSmooth * 2)
        : lerp(GAMMA_BASE, GAMMA_MIN, (this._lowSmooth - 0.5) * 2);
      this._gammaTarget = clamp(g / (tightening ? TIGHTEN_B : 1), GAMMA_MIN, GAMMA_MAX);
    }
    // --- the travelling ------------------------------------------------------
    // One turn of spiral (2*PI of u) crossed in exactly 4 beats. The boost is
    // never below 1: there is no state anywhere in this file that can make the
    // travelling run backwards.
    this._travelBoost += (this._travelBoostTarget - this._travelBoost) * (1 - Math.exp(-dt / 0.22));
    const omegaU = (TAU / (TRAVEL_BEATS_PER_TURN * beatS)) * this._speedScale;
    this._travel += omegaU * Math.max(1, this._travelBoost) * dt;
    // Kept inside one period: the shader adds u0 to it, and an accumulator that
    // grows for the length of a track is a precision problem, not a feature.
    this._travel = ((this._travel % THETA_SPAN) + THETA_SPAN) % THETA_SPAN;
    u.uTravel.value = this._travel;

    // The winding moves only as fast as the travelling can absorb (see the
    // header) - which is why this comes AFTER omegaU is known, not before.
    this._slewGamma(dt, omegaU);

    // --- differential rotation (bands.mid) -----------------------------------
    // The uniform is the ratio w0/OMEGA_U, which is what the closed form needs
    // and is bpm-independent by construction (both sides scale with 1/beatS).
    // Calibrated so a point at mid-radius turns once per 16 beats.
    // Clamped: at the tightest winding the calibration radius is five times
    // what it is at rest, and left alone the arms would pick up seven extra
    // turns of twist over one pass - unreadable, and 44 radians of angle for
    // the vertex shader to carry.
    const twistTarget = clamp(this._twistSign
      * (TRAVEL_BEATS_PER_TURN / ROT_BEATS_PER_TURN_MID)
      * (1 + ROT_R_COEF * this._rMid)
      * (0.6 + 0.8 * this._midSmooth), -TWIST_MAX, TWIST_MAX);
    // Slewed, never stepped: the sign flip on a drop has to pass through zero
    // as a visible unwinding, not as a frame where every arm jumps.
    this._twist += (twistTarget - this._twist) * (1 - Math.exp(-dt / 0.45));
    u.uTwist.value = this._twist;

    // A slow rigid spin on top, so the pattern itself turns and not only the
    // material running through it. Wrapped at 2*PI, which is exact.
    this._spin = (this._spin + (TAU / (32 * beatS)) * (0.5 + this._midSmooth) * dt) % TAU;
    u.uSpin.value = this._spin;

    // --- arms, on phrases ----------------------------------------------------
    const phraseIndex = profile.phraseIndex;
    if (this._forcedArms != null) {
      if (this._arms !== this._forcedArms) {
        this._armsPrev = this._arms;
        this._arms = this._forcedArms;
        this._armMix = 0;
        this._armMixRate = 1 / (ARM_MIX_BEATS * beatS);
        this._applyArmSelectors();
      }
    } else if (phraseIndex != null) {
      if (this._lastPhraseIndex != null && phraseIndex !== this._lastPhraseIndex) {
        this._armsPrev = this._arms;
        this._arms = this._pickArms();
        this._armMix = 0;
        this._armMixRate = 1 / (ARM_MIX_BEATS * beatS);
        this._applyArmSelectors();
      }
      this._lastPhraseIndex = phraseIndex;
    } else if (this._time > this._noGridPhraseAt) {
      // NO BEAT GRID, NO STANDING STILL: phraseIndex is null on a radio stream
      // and for the first seconds of any track. A plain timer takes over.
      this._noGridPhraseAt = this._time + 16 * beatS;
      this._armsPrev = this._arms;
      this._arms = this._pickArms();
      this._armMix = 0;
      this._armMixRate = 1 / (ARM_MIX_BEATS * beatS);
      this._applyArmSelectors();
    }
    if (this._armMix < 1) {
      this._armMix = Math.min(1, this._armMix + this._armMixRate * dt);
      // Smoothstep, so a point leaves its old arm and arrives at the new one
      // without either end of the migration being a corner.
      const t = this._armMix;
      u.uArmMix.value = t * t * (3 - 2 * t);
    } else {
      u.uArmMix.value = 1;
    }

    // --- the chromatic ramp migrates one notch per bar -----------------------
    const barIndex = profile.barIndex;
    if (barIndex != null) {
      if (this._lastBarIndex != null && barIndex !== this._lastBarIndex) {
        this._rampTarget += 1 / RAMP_STEPS;
      }
      this._lastBarIndex = barIndex;
    } else if (this._time > this._noGridBarAt) {
      this._noGridBarAt = this._time + 4 * beatS;
      this._rampTarget += 1 / RAMP_STEPS;
    }
    this._rampShift += (this._rampTarget - this._rampShift) * (1 - Math.exp(-dt / RAMP_SLEW_S));
    if (this._rampShift > 4 && this._rampTarget > 4) {
      // Both wrapped together at a whole number of notches, so the shift never
      // grows and fract() in the shader sees the same value it would have.
      this._rampShift -= 4;
      this._rampTarget -= 4;
    }
    u.uRampShift.value = this._rampShift;

    // --- beat: the wave along the arms, and the core's pulse -----------------
    u.uBeatPhase.value = profile.beatPhase != null
      ? profile.beatPhase
      : (this._time / beatS) % 1;
    const intensity = director && director.intensity != null ? director.intensity : 0.8;
    u.uIntensity.value = 0.55 + 0.55 * intensity;
    u.uWaveAmt.value = 0.7 + 0.5 * intensity;
    const pulse = director && director.pulse
      ? director.pulse(CORE_PULSE_AMOUNT, CORE_PULSE_DECAY)
      : 0;
    u.uCorePulse.value = pulse;
    this._coreUniforms.uAmount.value = (0.55 + 0.45 * intensity) * (1 + pulse * 2.2);

    // --- progress: how much vortex there is ----------------------------------
    // profile.progress, as the brief asks; on a radio stream duration is 0 and
    // there is no position to read, so the director's own envelope stands in
    // rather than leaving the vortex permanently at its floor.
    const prog = profile.duration > 0 ? clamp01(profile.progress) : intensity;
    u.uActiveFrac.value = 0.55 + 0.45 * Math.sin(Math.PI * clamp01(prog * 0.9 + 0.1));

    // --- dust (bands.high) ---------------------------------------------------
    u.uDustGain.value = 0.45 + 1.0 * this._highSmooth;
    u.uSparkle.value = 0.15 + 0.55 * this._highSmooth;
    this._twinkleT = ((this._twinkleT || 0) + dt * 3.1) % TAU;
    u.uTwinkle.value = this._twinkleT;

    // --- lateral drift: the vortex's centre leaves the frame's centre --------
    const a = (this._time / DRIFT_PERIOD_S) * TAU;
    u.uAxis.value.set(DRIFT_AMP * Math.sin(a), DRIFT_AMP * 0.6 * Math.cos(a * 0.73));
    this._core.position.set(u.uAxis.value.x, u.uAxis.value.y, -CORE_DEPTH);

    this._ditherFrame = (this._ditherFrame + 1) % 4;
    this._bgUniforms.uDither.value = this._ditherFrame;
  }

  dispose() {
    this._scene.remove(this._points);
    this._scene.remove(this._bg);
    this._scene.remove(this._core);
    this._points.geometry.dispose();
    this._pointsMat.dispose();
    this._bg.geometry.dispose();
    this._bgMat.dispose();
    this._core.geometry.dispose();
    this._coreMat.dispose();
    if (this._rampTex) this._rampTex.dispose();
  }
}
